// Partner ads: each cam network's banner and widgets, pasted in Admin → Settings → Ads. A page shows only
// the ads of the network it is about (the BongaCams streams tab: BongaCams; a LiveJasmin video: LiveJasmin)
// and pages about no single network (videos, "All platforms") the default network's. Shared by the server
// (which parses the codes) and the client (which places them).

export const AD_NETWORKS = ["bongacams", "chaturbate", "stripchat", "livejasmin", "cam4"] as const;
export type AdNetwork = (typeof AD_NETWORKS)[number];

export const AD_NETWORK_NAMES: Record<AdNetwork, string> = {
  bongacams: "BongaCams",
  chaturbate: "Chaturbate",
  stripchat: "Stripchat",
  livejasmin: "LiveJasmin",
  cam4: "CAM4",
};

/**
 * card: a banner shaped like a video card, in the video feeds and "Up next" (an iframe; adaptive banners
 * fill it). top: a 728×90 banner in the empty top-right of the live cams pages on wide screens (an
 * iframe, or for Stripchat its landing link: a strip of its live models from Stripcash's widget API). The rest are the network's own scripts, on computers only, so phones stay uncluttered:
 * footerBar (a strip of live models along the bottom), chatHead (a chat bubble in the corner),
 * instantMessage (a chat-style pop-up in the bottom-left corner, at most every 12 hours) and
 * popunder (opens the network behind the window on a click; the network caps how often). A popunder
 * can also be just the network's landing link (Stripcash's popunder is a script to fill in, not one to
 * load): the site then runs it itself (Ads.tsx, PopunderLink), at most every 12 hours.
 */
export const AD_FORMATS = ["card", "top", "footerBar", "chatHead", "instantMessage", "popunder"] as const;
export type AdFormat = (typeof AD_FORMATS)[number];

export const isAdNetwork = (v: unknown): v is AdNetwork => typeof v === "string" && (AD_NETWORKS as readonly string[]).includes(v);

/** The only hosts each network's codes may load from. */
const HOSTS: Record<AdNetwork, RegExp> = {
  bongacams: /(^|\.)(bngprm|bngdin|bngpop|bngpt|bongacash|bongacams\d*)\.com$/,
  chaturbate: /(^|\.)(chaturbate|highwebmedia)\.com$/,
  stripchat: /(^|\.)(stripchat|strpst|stripcdn|creative-stripchat|whitetrafsa)\.com$/,
  livejasmin: /(^|\.)(awempire|protoawe|ctwmsg|atwmcd|vcmdiawe|livejasmin)\.com$/,
  cam4: /(^|\.)(cam4|cam4pays|cam4tracking|cam4s)\.com$/,
};

export interface AdCode {
  /** "link": a landing page to open (a popunder the site runs itself, as Stripcash's is set up). */
  kind: "iframe" | "script" | "link";
  src: string;
  /** Some scripts find themselves by id (FOBAR_JS, IM_JS). */
  id?: string;
}

export type NetworkAds = Partial<Record<AdFormat, AdCode>>;

const entities = (s: string) => s.replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");

/** An https URL on one of the network's own hosts. */
function ownUrl(src: string, network: AdNetwork): boolean {
  try {
    const url = new URL(src);
    return url.protocol === "https:" && HOSTS[network].test(url.hostname);
  } catch {
    return false;
  }
}

/**
 * The <iframe> or <script src> of a pasted ad code; null unless it is one and loads over https from the
 * network's own hosts (so a code can't run anything else on the site). A bare landing link, or
 * Stripcash's popunder script with its landing link filled in (puURLstrpcht: '…'), is a "link": only
 * the link is kept, never the pasted script.
 */
export function parseAdCode(code: string, network: AdNetwork): AdCode | null {
  const link = code.trim().match(/^https:\/\/\S+$/)?.[0] ?? code.match(/puURLstrpcht\s*:\s*["']([^"']+)["']/)?.[1];
  if (link) return ownUrl(entities(link), network) ? { kind: "link", src: entities(link) } : null;
  const tag = code.match(/<(iframe|script)\b([^>]*)>/i);
  if (!tag) return null;
  const attr = (name: string) => {
    const m = tag[2].match(new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i"));
    return m ? entities(m[1] ?? m[2] ?? m[3] ?? "") : undefined;
  };
  const src = attr("src");
  if (!src || !ownUrl(src, network)) return null;
  const id = attr("id");
  return { kind: tag[1].toLowerCase() as AdCode["kind"], src, ...(id && /^[\w-]{1,40}$/.test(id) && { id }) };
}

/** The network a page is about: the streams platform it shows (/cams/<id>, same ids), else the default one. */
export function adNetworkFor(pathname: string, platform: string | null, fallback: AdNetwork | null): AdNetwork | null {
  const page = pathname.match(/^\/cams\/([a-z0-9]+)/)?.[1] ?? (pathname === "/" ? platform : null);
  return isAdNetwork(page) ? page : fallback;
}

/** Whether a stack trace comes from one of these hosts' scripts (to tell an ad script's listeners apart). */
export const fromHosts = (stack: string | undefined, hosts: string[]) => !!stack && hosts.some((h) => stack.includes(`//${h}/`));

/** What the site's pages get: each network's usable codes, and the default network. */
export interface PageAds {
  defaultNetwork: AdNetwork | null;
  networks: Partial<Record<AdNetwork, NetworkAds>>;
}

export function parseAds(settings: {
  defaultNetwork: string;
  networks: Record<AdNetwork, Record<AdFormat, string>>;
  paused?: Partial<Record<AdNetwork, AdFormat[]>>;
}): PageAds {
  const networks: PageAds["networks"] = {};
  for (const network of AD_NETWORKS) {
    const ads: NetworkAds = {};
    for (const format of AD_FORMATS) {
      if (settings.paused?.[network]?.includes(format)) continue;
      const code = parseAdCode(settings.networks[network]?.[format] ?? "", network);
      // Banners are frames, the rest scripts; a popunder may be a landing link, and so may Stripchat's top
      // banner (a strip of its live models, Ads.tsx).
      const fits =
        format === "card" || format === "top"
          ? code?.kind === "iframe" || (format === "top" && network === "stripchat" && code?.kind === "link")
          : code?.kind === "script" || (format === "popunder" && code?.kind === "link");
      if (code && fits) ads[format] = code;
    }
    if (Object.keys(ads).length) networks[network] = ads;
  }
  return { defaultNetwork: isAdNetwork(settings.defaultNetwork) ? settings.defaultNetwork : null, networks };
}
